import { type Component, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import { type Path, useNavigate } from '~/router'
import { type CartLineType, cartLines, cartSubtotal, removeFromCart, setLineQuantity } from '~/stores/cart'

/**
 * El carrito, en un cajón.
 *
 * Se abre al añadir algo y no saca al comprador de la ficha: seguir mirando el
 * catálogo con lo que ya llevas a la vista es lo que hace que se compre más de
 * una cosa. Para revisar con calma está la página `/carrito`.
 *
 * Los precios de aquí son los que se guardaron al añadir, así que son
 * orientativos: **el total que se cobra lo calcula el backend** contra el
 * catálogo del momento de pagar.
 */
export interface CartDrawerPropsType {
  empresa: string
  isOpen: boolean
  onClose: () => void
  /** Cómo formatear el dinero con la moneda y el locale de la empresa. */
  formatPrice: (value: number, currency: string) => string
}

const CartDrawer: Component<CartDrawerPropsType> = props => {
  const navigate = useNavigate()
  const lines = () => cartLines(props.empresa)
  const currency = () => lines()[0]?.currency ?? 'COP'

  const go = (path: string) => {
    props.onClose()
    navigate(path as Path)
  }

  const step = (line: CartLineType, delta: number) => setLineQuantity(props.empresa, line.productId, line.variantId, line.quantity + delta)

  return (
    <Drawer isOpen={props.isOpen} onClose={props.onClose} position="right" title="Tu carrito">
      <div class="flex h-full flex-col">
        <Show
          when={lines().length}
          fallback={
            <div class="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <Icon icon="material-symbols-light:shopping-cart-outline" size={44} class="text-foreground-subtle" />
              <p class="text-foreground-subtle">Todavía no has añadido nada.</p>
              <Button variant="outlined" color="default" onClick={props.onClose}>
                Seguir mirando
              </Button>
            </div>
          }
        >
          <div class="flex-1 overflow-y-auto space-y-3 pb-4">
            <For each={lines()}>
              {line => (
                <div class="flex gap-3 rounded-lg border border-border p-3">
                  <Show when={line.image}>
                    <img src={line.image as string} alt={line.name} class="h-16 w-16 shrink-0 rounded-md object-cover" />
                  </Show>

                  <div class="min-w-0 flex-1">
                    <p class="font-medium text-foreground leading-tight">{line.name}</p>
                    <Show when={line.variantLabel}>
                      <p class="text-xs text-foreground-subtle">{line.variantLabel}</p>
                    </Show>
                    <p class="text-sm text-foreground-subtle">{props.formatPrice(line.unitPrice, line.currency)} c/u</p>

                    <div class="mt-2 flex items-center justify-between gap-2">
                      <div class="flex items-center gap-1 rounded-md border border-border">
                        <Button variant="text" color="default" size="sm" icon="material-symbols-light:remove" aria-label="Quitar uno" onClick={() => step(line, -1)} />
                        <span class="min-w-6 text-center text-sm">{line.quantity}</span>
                        <Button
                          variant="text"
                          color="default"
                          size="sm"
                          icon="material-symbols-light:add"
                          aria-label="Añadir uno"
                          // Con existencias controladas no se deja pasar del tope:
                          // enterarse al pagar es peor que no poder subir aquí.
                          disabled={line.trackInventory && line.stock > 0 && line.quantity >= line.stock}
                          onClick={() => step(line, 1)}
                        />
                      </div>

                      <div class="flex items-center gap-2">
                        <span class="font-semibold text-foreground">{props.formatPrice(line.unitPrice * line.quantity, line.currency)}</span>
                        <Button
                          variant="text"
                          color="danger"
                          size="sm"
                          icon="material-symbols-light:delete-outline"
                          tooltip="Quitar"
                          aria-label={`Quitar ${line.name}`}
                          onClick={() => removeFromCart(props.empresa, line.productId, line.variantId)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>

          <div class="border-t border-border pt-3 space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-foreground-subtle">Subtotal</span>
              <span class="text-lg font-bold text-foreground">{props.formatPrice(cartSubtotal(props.empresa), currency())}</span>
            </div>
            <p class="text-xs text-foreground-subtle">Los impuestos y el total definitivo se calculan al pagar.</p>

            <div class="flex flex-col gap-2">
              <Button fullWidth size="md" prependIcon="material-symbols-light:lock-outline" onClick={() => go(`/catalogo/${props.empresa}/pago`)}>
                Pagar
              </Button>
              <Button fullWidth size="md" variant="outlined" color="default" onClick={() => go(`/catalogo/${props.empresa}/carrito`)}>
                Ver carrito
              </Button>
            </div>
          </div>
        </Show>
      </div>
    </Drawer>
  )
}

export default CartDrawer
